/* Stealth EU Admin, shared shell JS (ported from UK ag-admin.js).
   Initialised by AG-Admin-Shell.liquid via window.AG_ADMIN_CONFIG (set
   inline in the section). Provides:
     - Password auth gate, persisted in localStorage
     - window.AG_ADMIN.api(action, payload) → fetch helper for tools
     - Toast notifications
     - Tool nav (top-bar dropdown)
   Each tool section sets data-ag-tool="<name>" on its container; the
   shell hides them until auth passes. */

(function () {
  const cfg = window.AG_ADMIN_CONFIG || {};
  const apiUrl = cfg.apiUrl || '';
  const pwdKey = 'agAdminPwd:' + (cfg.scope || 'default');

  /* Genuine READS that the isWrite regex would otherwise misclassify (e.g.
     "image" matches get_images) or that lack a read prefix (e.g. "list"), so
     they'd never retry on an Apps Script cold start. Force them to the read
     (retryable) path. Only add here actions that are unambiguously idempotent
     reads, never a mutation. */
  const FORCE_READ = { get_images: 1, list: 1, seo_list: 1, seo_get_images: 1, seo_get_history: 1 };

  /* The mirror of FORCE_READ: genuine MUTATIONS the isWrite regex misses, so
     they are never retried, never deduped, and get the full write deadline.
     seo_run_audit walks the whole catalogue and then APPENDS a snapshot to the
     audit history, so a retry would double-record it and the 60s read deadline
     would abort a run that is still working. */
  const FORCE_WRITE = { seo_run_audit: 1 };

  /* Read/write classifier. ONE definition, used by the proxy path, the direct
     path, and the single-flight dedupe so the three can never disagree about
     what is safe to retry or share. Mutations are never retried or deduped.
     (Ported from UK PR #304.) */
  const HEAVY_READS = { ui_profitability: 1, ui_sales: 1, ui_channels: 1, ui_discounts: 1 };
  function classifyAction(action) {
    const isWrite = !!FORCE_WRITE[action] || (!FORCE_READ[action] && /save|delete|adjust|receive|sync|pull|create|recompute|reset|seed|provision|enqueue|add|remove|_reorder|rename|import|restore|backup|bulk|mint|toggle|enable|disable|update|move|_set|set_|active|image|upload|paste|signoff|pause|log_/i.test(action));
    const isRead = !!FORCE_READ[action] || (!isWrite && (/^(ping|admin_ping|admin_list|ui_|list_|get_|read_|bootstrap)/i.test(action) || HEAVY_READS[action]));
    return { isWrite: isWrite, isRead: isRead };
  }

  /* fetch with a hard deadline. AG_ADMIN.api previously had NO timeout, so a
     call that outran the Apps Script ~6-min wall left its promise unsettled
     forever: the UI span forever and a slow call was indistinguishable from a
     dead one (the 2026-08-06 "PO won't save" incident). Reads get a shorter
     deadline (retry rides out a cold start); writes get the near-full server
     wall, because aborting the CLIENT does not stop the server and a retry
     could double-execute. */
  async function fetchWithDeadline(url, opts, ms) {
    const ctl = ('AbortController' in window) ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), ms) : null;
    try {
      return await fetch(url, ctl ? Object.assign({}, opts, { signal: ctl.signal }) : opts);
    } catch (e) {
      if (e && (e.name === 'AbortError' || /abort/i.test(String(e.message || e)))) {
        throw new Error('Timed out after ' + Math.round(ms / 1000) + 's waiting for the admin backend. It may still be finishing server-side. Try again in a minute.');
      }
      throw e;
    } finally { if (timer) clearTimeout(timer); }
  }
  const READ_DEADLINE_MS = 60000, HEAVY_READ_DEADLINE_MS = 100000, WRITE_DEADLINE_MS = 300000;
  function deadlineFor(action) {
    const c = classifyAction(action);
    if (c.isWrite) return WRITE_DEADLINE_MS;
    return HEAVY_READS[action] ? HEAVY_READ_DEADLINE_MS : READ_DEADLINE_MS;
  }

  /* Edge fast-path (D1 Worker). Read-only inventory tabs with NO params are
     served from the edge cache (<100ms) instead of the ~2s Apps Script /exec.
     ANY failure falls through to /exec, so it can never break data loading.
     Freshness: edge is refreshed by the keep-warm (~15 min), fine for browsing;
     writes + custom date ranges always use /exec.
     SCOPE GATE: the edge worker only holds the inventory/analytics tool's data
     (scope 'default'). Other tools share this shell but reuse generic action
     names, notably the Bundle Builder also calls `ui_bootstrap`, so without a
     scope gate they'd read inventory's edge payload (no shop/health → a phantom
     "Functions not ready" banner). Only the inventory scope may consult the edge. */
  const EDGE_BASE = cfg.edgeUrl || ''; /* EU: ag-analytics-d1-eu, set on admin-stock, admin-analytics and admin-inventory; proxied sections read it via the Worker proxy instead */
  const EDGE_ACTIONS = { ui_today: 1, ui_bootstrap: 1, ui_stock: 1, ui_preorders: 1, ui_bis: 1, ui_customers: 1, ui_cohorts: 1 };
  const EDGE_SCOPE_OK = (cfg.scope || 'default') === 'default';

  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const $ = (sel, root) => (root || document).querySelector(sel);

  /* Real authenticated flag. onReady() gates on THIS, not the auth
     overlay's CSS class, the overlay starts `hidden` in markup, so a
     class check read "hidden" as "already authed" at script-parse time
     and ran a tool's boot (→ a premature ui_bootstrap) before login
     completed. That spurious call painted "Failed to load: auth" AND
     burned a strike on the backend's GLOBAL brute-force lockout, which
     on the shipping scope can also collateral-block the Worker's live
     rate-data sync. */
  let authed = false;

  function getStoredPwd() { try { return localStorage.getItem(pwdKey) || ''; } catch (_) { return ''; } }
  function setStoredPwd(v) { try { v ? localStorage.setItem(pwdKey, v) : localStorage.removeItem(pwdKey); } catch (_) {} }

  /* Edge-bypass. PERSISTED so it survives a page reload. After a write the
     edge cache is stale until the next keep-warm (~30min); without persistence the
     bypass lived only in memory, so a reload read the stale edge and the edit
     looked like it "reverted / didn't save".

     ☠ The bypass used to be a single GLOBAL 31-minute window: ANY write disabled
     the edge for EVERY read. An operator actually working (saving POs, adjusting
     stock every few minutes) kept re-arming it, so the <300ms edge path was off
     precisely while they used the tool and every tab click paid the full /exec
     round trip. Now PER-KEY (ported from UK PR #304 the day the EU edge went
     live, 2026-08-06): a write bypasses only the edge keys whose data it
     touches; unrelated tabs keep their fast path. An action we can't map falls
     back to bypassing everything, which is the old (safe) behaviour. The legacy
     global localStorage value is still honoured until it expires so a
     mid-rollout reload can't read a stale edit. */
  function _bypassMap() {
    try { return JSON.parse(localStorage.getItem('agEdgeBypassMap') || '{}') || {}; } catch (_) { return {}; }
  }
  function _saveBypassMap(m) { try { localStorage.setItem('agEdgeBypassMap', JSON.stringify(m)); } catch (_) {} }
  function _legacyBypassActive() {
    if (window.__agEdgeBypassUntil && Date.now() < window.__agEdgeBypassUntil) return true;
    try { const bp = parseInt(localStorage.getItem('agEdgeBypassUntil'), 10); if (bp && Date.now() < bp) { window.__agEdgeBypassUntil = bp; return true; } } catch (_) {}
    return false;
  }
  /* Is the edge bypassed for THIS key? key null/undefined = "any" (used when a
     caller can't name a key: any active bypass wins, old semantics). */
  function edgeBypassActive(key) {
    if (_legacyBypassActive()) return true;
    const m = _bypassMap();
    if (m['*'] && Date.now() < m['*']) return true;
    if (!key) return Object.keys(m).some(k => Date.now() < m[k]);
    return !!(m[key] && Date.now() < m[key]);
  }
  /* keys: array of edge keys, or '*' for everything. */
  function setEdgeBypass(ms, keys) {
    const until = Date.now() + ms;
    const m = _bypassMap();
    Object.keys(m).forEach(k => { if (m[k] <= Date.now()) delete m[k]; }); // prune expired
    (keys === '*' || !keys ? ['*'] : keys).forEach(k => { m[k] = Math.max(m[k] || 0, until); });
    _saveBypassMap(m);
  }
  /* Which edge keys does a write invalidate? Grouped by data family, matching
     what the backend rebuilds. Unlisted writes → '*' (bypass everything). */
  const EDGE_TOUCHES = [
    /* PO lifecycle + stock + SKU settings → the inventory family. */
    { re: /po|receive|signoff|preorder|adjust_on_hand|set_unit_cost|set_vendor|sku_settings|archive_product|delete_product|loss|bom|cost_layer|stock/i,
      keys: ['ui_bootstrap', 'ui_stock', 'ui_today', 'ui_preorders', 'ui_bis'] },
    /* Marketing / operating costs / ad spend → the money-analytics family. */
    { re: /marketing|operating|ad_spend|adspend|paste_ad|subscription/i,
      keys: ['ui_today', 'ui_sales', 'ui_profitability', 'ui_channels', 'ui_discounts'] },
  ];
  function edgeKeysTouchedBy(action) {
    for (let i = 0; i < EDGE_TOUCHES.length; i++) if (EDGE_TOUCHES[i].re.test(action)) return EDGE_TOUCHES[i].keys;
    return '*';
  }

  /* Phase 2, gate + proxy. Sections listed in cfg.proxySections send their
     tool calls THROUGH the auth Worker (POST /api/<section>), which verifies
     the session token (or break-glass shared password) + section, then forwards
     to the real backend server-side. The tool's own code is unchanged, the
     proxy passes the backend's JSON straight back. Read retries mirror the
     direct path so an Apps-Script cold start still rides out. */
  async function apiViaProxy(section, action, payload) {
    const session = getSession();
    const token = session && session.token;
    const body = Object.assign({ action: action }, payload || {});
    if (!token) body.password = getStoredPwd(); // break-glass (no OTP session)
    // Inventory keeps the D1 edge cache (served in the Worker). After a write we
    // set a client-side bypass window; while active, tell the Worker to read
    // fresh (_noEdge) so an edit doesn't read stale cache and look reverted.
    const INV = section === 'stock' || section === 'analytics' || section === 'inventory';
    /* Per-key: only tell the Worker to skip the edge when the bypass covers the
       key THIS read would be served from. A read the edge can't serve anyway
       (params, non-edge action) never needs _noEdge. */
    const _proxyEdgeKey = (payload && payload._edge) ? String(payload._edge)
      : (EDGE_ACTIONS[action] && (!payload || !Object.keys(payload).length)) ? action
      : (action === 'ui_profitability' && payload && payload.since_cutover === true && Object.keys(payload).length === 1) ? 'ui_profitability'
      : null;
    if (INV && _proxyEdgeKey && edgeBypassActive(_proxyEdgeKey)) body._noEdge = 1;
    const RETRY_ACTIONS = { ui_profitability: 1, ui_sales: 1, ui_channels: 1, ui_discounts: 1 };
    // Only READS may be retried. Mutations are never retried, a retry after a
    // write whose response was lost would double-execute it (e.g. duplicate a
    // bundle variant / double-delete). Any action naming a mutation is 1-try.
    const _cls = classifyAction(action);
    const isWrite = _cls.isWrite, isRead = _cls.isRead;
    const maxTries = isRead ? 4 : 1;
    let lastErr, wokeToast = false;
    for (let attempt = 1; attempt <= maxTries; attempt++) {
      try {
        const res = await fetchWithDeadline(authUrl + '/api/' + encodeURIComponent(section), {
          method: 'POST',
          headers: Object.assign({ 'Content-Type': 'text/plain;charset=utf-8' }, token ? { 'Authorization': 'Bearer ' + token } : {}),
          body: JSON.stringify(body),
          redirect: 'follow'
        }, deadlineFor(action));
        if (res.status === 401) { setStoredPwd(''); setSession(null); showAuth('email'); throw new Error('auth'); }
        if (!res.ok && res.status !== 403) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        if (data && data.error === 'auth') {
          // A 401 above = the auth Worker rejected our session. Reaching HERE
          // (HTTP 200, body {error:auth}) while holding a valid token means the
          // OTP session is fine but the TOOL BACKEND rejected the injected staff
          // password, don't sign the user out; tell an admin to set its secret.
          if (token) throw new Error('This tool’s backend rejected the staff password. An admin needs to set its TOOL_*_PW secret on stealth-eu-admin-auth.');
          setStoredPwd(''); setSession(null); showAuth('email'); throw new Error('auth');
        }
        if (data && data.error === 'forbidden') throw new Error('You don’t have access to this section.');
        if (data && data.error) throw new Error(data.error);
        if (isWrite && INV) setEdgeBypass(31 * 60 * 1000, edgeKeysTouchedBy(action)); // edits read fresh (only the touched keys) until the edge refreshes
        return data;
      } catch (e) {
        lastErr = e;
        const msg = String(e && e.message || e);
        if (msg === 'auth') throw e;
        const transient = (e instanceof TypeError) ||
          /timed out after/i.test(msg) ||
          /failed to fetch|networkerror|load failed/i.test(msg) ||
          /unexpected token|not valid json|in json|json\.parse|syntax/i.test(msg) ||
          /^HTTP 5\d\d/.test(msg);
        if (attempt < maxTries && isRead && transient) {
          if (!wokeToast) { try { toast('Waking the admin backend… one moment', false); } catch (_) {} wokeToast = true; }
          const delay = RETRY_ACTIONS[action] ? 30000 : [2500, 7000, 16000][attempt - 1] || 16000;
          await new Promise(r => setTimeout(r, delay));
          continue;
        }
        throw e;
      }
    }
    throw lastErr;
  }

  /* Edge read: the D1 Worker's cached payload for edgeKey, or null on ANY
     failure (no password, miss, refused, network, bad JSON) so the caller falls
     through to /exec. The admin password rides in the X-Admin-Password header,
     never the URL (2026-10-09, port of UK #757): a query string lands in
     browser history and in request logs. URI-encoded, as the old ?pw= was, so
     any character survives a header. The edge Worker (ag-analytics-d1-eu)
     answers the browser's preflight for that header. */
  async function edgeGet(edgeKey) {
    const pw = getStoredPwd();
    if (!pw) return null;   // the edge would only refuse it
    try {
      const er = await fetch(EDGE_BASE + '/ui/get?key=' + encodeURIComponent(edgeKey),
        { method: 'GET', headers: { 'X-Admin-Password': encodeURIComponent(pw) } });
      if (!er.ok) return null;
      const ed = await er.json();
      return (ed && ed.ok && ed.payload !== undefined && ed.payload !== null) ? ed.payload : null;
    } catch (_) { return null; }
  }

  /* api(action, payload). JSON POST to Apps Script /exec. Auto-injects
     the stored password. Resolves on { ok:true, ... }; rejects on
     non-2xx or { error: ... } in the body. */
  /* Single-flight dedupe for READS. Two concurrent identical reads (e.g. rapid
     tab-flipping re-requesting a tab already in flight) used to race as
     separate calls: doubled load on the script, and the concurrent-duplicate
     pattern was observed 404ing outright on UK (2026-08-06/07). Identical
     concurrent reads now share one promise. Writes are NEVER deduped: two
     intentional submits must both execute. (Ported from UK PR #304.) */
  const _inflightReads = {};
  function api(action, payload) {
    if (!classifyAction(action).isRead) return _apiRoute(action, payload);
    let k;
    try { k = action + '|' + JSON.stringify(payload || {}); } catch (_) { return _apiRoute(action, payload); }
    if (_inflightReads[k]) return _inflightReads[k];
    const p = _apiRoute(action, payload);
    _inflightReads[k] = p;
    const clear = () => { if (_inflightReads[k] === p) delete _inflightReads[k]; };
    p.then(clear, clear);
    return p;
  }

  async function _apiRoute(action, payload) {
    /* Proxied sections route through the auth Worker (server-side enforcement).
       Every other tool falls through to the unchanged direct path below. */
    const _section = (Array.isArray(cfg.tools) ? (cfg.tools.find(t => t.current) || {}) : {}).section;
    if (_section && Array.isArray(cfg.proxySections) && cfg.proxySections.indexOf(_section) > -1 && authUrl) {
      return apiViaProxy(_section, action, payload);
    }
    if (!apiUrl) throw new Error('Admin API URL not configured');
    /* Edge fast-path: read-only tab, no params → try the D1 edge cache first.
       BUT skip the edge for ~31min after any write, the edge cache only
       refreshes on the keep-warm (~30min), so a fresh edit wouldn't show
       (it'd read stale and "revert"). During the bypass window reads go to
       /exec (fresh) until the keep-warm repopulates the edge. */
    const noParams = !payload || !Object.keys(payload).length;
    /* The Profitability tab's DEFAULT (since-cutover) is kept warm + pushed to
       the edge key 'ui_profitability' by the keep-warm, so serve it from the
       edge too (it has params, so it's not a plain EDGE_ACTION). */
    const isDefaultProfit = action === 'ui_profitability' && payload && payload.since_cutover === true && Object.keys(payload).length === 1;
    /* Explicit edge hint: a caller that KNOWS its request matches a warmed edge
       view (e.g. the default-range Sales tab) passes `_edge:'<key>'` so we serve
       it from the edge even though it carries params. Stripped from the /exec body. */
    const hintedEdge = (payload && payload._edge) ? String(payload._edge) : null;
    const edgeKey = hintedEdge || ((EDGE_ACTIONS[action] && noParams) ? action : (isDefaultProfit ? 'ui_profitability' : null));
    if (EDGE_SCOPE_OK && EDGE_BASE && edgeKey && !edgeBypassActive(edgeKey)) {
      const edged = await edgeGet(edgeKey);
      if (edged !== null) return edged;   // null: fall through to /exec
    }
    const body = Object.assign({ action, password: getStoredPwd() }, payload || {});
    if (body._edge !== undefined) delete body._edge; // edge-routing hint only; not a backend param
    /* Custom-range analytics tabs recompute server-side (~1 min) and the proxy
       can drop the connection before it responds ("Failed to fetch"), but the
       server FINISHES and caches the result. So for these idempotent reads,
       retry a few times; a later attempt hits the cached result. Never retry
       writes or explicit server errors. */
    /* Stability: retry every idempotent READ through transient failures
       (network drop, cold-start HTML/non-JSON, HTTP 5xx) with an escalating
       backoff that rides out an Apps Script cold start; never retry writes.
       Stops a single cold-start/redeploy hiccup from reading as "admin won't
       load" / a random error. `ui_profitability/sales/channels/discounts`
       keep the longer 30s backoff for their ~1-min server recompute. */
    const RETRY_ACTIONS = { ui_profitability: 1, ui_sales: 1, ui_channels: 1, ui_discounts: 1 };
    // Only READS may be retried. Mutations are never retried, a retry after a
    // write whose response was lost would double-execute it (e.g. duplicate a
    // bundle variant / double-delete). Any action naming a mutation is 1-try.
    const _cls = classifyAction(action);
    const isWrite = _cls.isWrite, isRead = _cls.isRead;
    const maxTries = isRead ? 4 : 1;
    let lastErr, wokeToast = false;
    for (let attempt = 1; attempt <= maxTries; attempt++) {
      try {
        const res = await fetchWithDeadline(apiUrl, {
          method: 'POST',
          // Plain text to keep CORS preflight off (Apps Script /exec quirk).
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(body),
          redirect: 'follow'
        }, deadlineFor(action));
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        if (data && data.error === 'auth') { setStoredPwd(''); showAuth(); throw new Error('auth'); }
        if (data && data.error) throw new Error(data.error);
        /* A write just landed → the edge cache is now stale until the next
           keep-warm. Bypass the edge (only the touched keys) so the change
           shows immediately; unrelated tabs keep the fast path. */
        if (isWrite || /d1_/i.test(action)) {
          setEdgeBypass(31 * 60 * 1000, edgeKeysTouchedBy(action));
        }
        return data;
      } catch (e) {
        lastErr = e;
        const msg = String(e && e.message || e);
        if (msg === 'auth') throw e; // real wrong-password, never retry
        const transient =
          (e instanceof TypeError) ||
          /timed out after/i.test(msg) ||
          /failed to fetch|networkerror|load failed/i.test(msg) ||
          /unexpected token|not valid json|in json|json\.parse|syntax/i.test(msg) ||
          /^HTTP 5\d\d/.test(msg);
        if (attempt < maxTries && isRead && transient) {
          if (!wokeToast) { try { toast('Waking the admin backend… one moment', false); } catch (_) {} wokeToast = true; }
          const delay = RETRY_ACTIONS[action] ? 30000 : [2500, 7000, 16000][attempt - 1] || 16000;
          await new Promise(r => setTimeout(r, delay));
          continue;
        }
        throw e;
      }
    }
    throw lastErr;
  }

  /* Toast */
  let toastEl = null;
  function ensureToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'ag-toast';
    document.body.appendChild(toastEl);
    return toastEl;
  }
  function toast(msg, isError) {
    const t = ensureToast();
    t.textContent = msg;
    t.classList.toggle('error', !!isError);
    t.classList.add('show');
    clearTimeout(toast._h);
    toast._h = setTimeout(() => t.classList.remove('show'), 3000);
  }

  /* ── Session (email + one-time code) ────────────────────────────
     The OTP flow proves WHO you are and yields your section list. The
     shared "tool password" (toolPw, returned by the auth Worker to
     authenticated users) still drives each tool backend unchanged,
     stored where api() already reads it, until the Phase 2 proxy takes
     over. Break-glass: the original shared-password path stays as a
     fallback (full access, no per-section gating). */
  const authUrl = String(cfg.authUrl || '').replace(/\/+$/, '');
  const SESSION_KEY = 'agAdminSession';
  const nowS = () => Math.floor(Date.now() / 1000);
  function getSession() { try { return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); } catch (_) { return null; } }
  function setSession(s) { try { s ? localStorage.setItem(SESSION_KEY, JSON.stringify(s)) : localStorage.removeItem(SESSION_KEY); } catch (_) {} }

  async function authApi(path, body, token) {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = 'Bearer ' + token;
    const res = await fetch(authUrl + path, { method: 'POST', headers, body: JSON.stringify(body || {}) });
    let data = {}; try { data = await res.json(); } catch (_) {}
    return { status: res.status, data: data };
  }

  /* Tool visibility */
  function hideTools() { $$('[data-ag-tool]').forEach(el => el.style.display = 'none'); }
  function revealTools() {
    authed = true;
    $$('[data-ag-tool]').forEach(el => el.style.display = '');
    window.dispatchEvent(new CustomEvent('ag-admin-ready'));
  }
  function hideAuthOverlay() { const el = $('#ag-admin-auth'); if (el) el.classList.add('hidden'); }

  /* Permissions. allowed === null → allow everything (superadmin / break-glass). */
  function allowedSections(session) {
    if (!session) return null;
    if (session.canManage) return null;
    return Array.isArray(session.sections) ? session.sections : [];
  }
  function sectionAllowed(section, allowed) {
    if (!section) return true;          // Hub / unlabelled
    if (allowed === null) return true;
    return allowed.indexOf(section) > -1;
  }

  /* Reflect the session's sections onto the hub tiles + nav dropdown. */
  function applyGating(session) {
    const allowed = allowedSections(session);
    const canManage = !!(session && session.canManage); // superadmin OTP session
    $$('.ag-hub-card[data-section]').forEach(card => {
      const sec = card.getAttribute('data-section');
      // The Access tile is superadmin-only (needs a token to manage users);
      // break-glass / members never see it.
      card.hidden = sec === 'access' ? !canManage : !sectionAllowed(sec, allowed);
    });
    $$('.ag-hub-group').forEach(g => {
      g.style.display = g.querySelector('.ag-hub-card:not([hidden])') ? '' : 'none';
    });
    renderToolNav(allowed);
  }

  /* Member opened a section they weren't granted (direct URL). Soft block;
     the hard server-side block lands in Phase 2. */
  function showNoAccess() {
    hideTools();
    let el = $('#ag-admin-noaccess');
    if (!el) {
      el = document.createElement('div');
      el.id = 'ag-admin-noaccess';
      el.className = 'ag-admin-noaccess';
      el.innerHTML = '<div class="ag-na-card">' +
        '<div class="ag-na-emblem">⛔</div>' +
        '<h1>No access</h1>' +
        '<p>Your account doesn’t have access to this section. Ask an admin to enable it, or head back to the hub.</p>' +
        '<a class="ag-btn" href="/pages/admin">← Back to Hub</a></div>';
      document.body.appendChild(el);
    }
    el.style.display = 'flex';
  }

  /* Once a session (or break-glass) is established: gate nav + tiles, then
     reveal the current tool or show the no-access panel. */
  function enterAdmin(session) {
    hideAuthOverlay();
    applyGating(session);
    const current = Array.isArray(cfg.tools) ? cfg.tools.find(t => t.current) : null;
    const section = current && current.section;
    if (!sectionAllowed(section, allowedSections(session))) { showNoAccess(); return; }
    revealTools();
  }

  /* ── Auth overlay: email → code, plus break-glass password ─────── */
  function showAuth(step) {
    authed = false;
    const el = $('#ag-admin-auth');
    if (!el) return;
    el.classList.remove('hidden');
    hideTools();
    setAuthStep(step || 'email');
  }
  function setAuthStep(step) {
    $$('#ag-admin-auth .ag-auth-form').forEach(f => f.classList.toggle('hidden', f.getAttribute('data-step') !== step));
    const alt = $('#ag-admin-auth .ag-auth-alt'); if (alt) alt.style.display = step === 'password' ? 'none' : '';
    const err = $('#ag-admin-auth .err'); if (err) err.textContent = '';
    setTimeout(() => { const inp = $('#ag-admin-auth .ag-auth-form:not(.hidden) input'); if (inp) inp.focus(); }, 40);
  }
  function setBusy(form, on, labelBusy) {
    const btn = form.querySelector('.ag-auth-submit'); const lbl = form.querySelector('.ag-auth-lbl');
    if (!btn) return;
    btn.classList.toggle('loading', on); btn.disabled = on;
    if (lbl) { if (on) { lbl.dataset.idle = lbl.textContent; lbl.textContent = labelBusy || 'Working…'; } else if (lbl.dataset.idle) { lbl.textContent = lbl.dataset.idle; } }
  }

  let pendingEmail = '';
  async function doRequestCode(email) {
    pendingEmail = email;
    await authApi('/auth/request', { email }); // always ok (anti-enumeration)
    const echo = $('#ag-admin-auth .ag-auth-email-echo'); if (echo) echo.textContent = email;
    setAuthStep('code');
  }
  async function doVerify(email, code) {
    const { data } = await authApi('/auth/verify', { email, code });
    if (data && data.ok && data.token) {
      const session = { token: data.token, email: data.email, role: data.role, sections: data.sections || [], canManage: !!data.canManage, exp: data.exp };
      setSession(session);
      setStoredPwd(data.toolPw || ''); // bridge, tools keep using the shared password
      enterAdmin(session);
      return { ok: true };
    }
    const err = (data && data.error) || 'invalid';
    const msg = err === 'expired' ? 'That code expired. Send a new one.'
      : err === 'locked' ? 'Too many tries. Send a new code.'
      : err === 'no_access' ? 'This email doesn’t have admin access.'
      : (data && typeof data.remaining === 'number' ? 'Incorrect code. ' + data.remaining + ' tries left.' : 'Incorrect code.');
    return { ok: false, message: msg };
  }
  /* Break-glass: original shared-password path (full access, no session).
     Validated against the auth Worker (/auth/breakglass) instead of a tool
     backend, so it works on EVERY admin page, including the gate-only
     (Library/Plans) and proxied (SEO/Post Editor) pages whose shell carries no
     api_url (which previously dead-ended here). */
  async function attemptPassword(pwd) {
    if (!authUrl) { return { ok: false, message: 'Admin auth is not configured on this page.' }; }
    try {
      const { status, data } = await authApi('/auth/breakglass', { password: pwd });
      if (data && data.ok) {
        setStoredPwd(data.toolPw || pwd);
        setSession(null);
        enterAdmin(null);
        return { ok: true };
      }
      if (status === 429 || (data && data.error === 'locked')) {
        const mins = Math.max(1, Math.ceil(((data && data.retryAfter) || 900) / 60));
        return { ok: false, message: 'Too many attempts. Try again in about ' + mins + ' min, or use the one-time code.' };
      }
      if (status === 401 || (data && data.error === 'invalid')) return { ok: false, message: 'Incorrect password.' };
      return { ok: false, message: 'Sign-in failed: ' + ((data && data.error) || ('HTTP ' + status)) };
    } catch (e) {
      return { ok: false, message: 'Could not reach the admin server. Try again.' };
    }
  }

  function wireAuth() {
    const root = $('#ag-admin-auth'); if (!root) return;
    const err = root.querySelector('.err');
    const emailForm = root.querySelector('.ag-auth-form[data-step="email"]');
    const codeForm = root.querySelector('.ag-auth-form[data-step="code"]');
    const pwForm = root.querySelector('.ag-auth-form[data-step="password"]');

    emailForm && emailForm.addEventListener('submit', async (e) => {
      e.preventDefault(); err.textContent = '';
      const email = (emailForm.querySelector('input').value || '').trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { err.textContent = 'Enter a valid email.'; return; }
      setBusy(emailForm, true, 'Sending…');
      try { await doRequestCode(email); } catch (_) { err.textContent = 'Network error. Try again.'; } finally { setBusy(emailForm, false); }
    });
    codeForm && codeForm.addEventListener('submit', async (e) => {
      e.preventDefault(); err.textContent = '';
      const code = (codeForm.querySelector('input').value || '').replace(/\D/g, '');
      if (code.length !== 6) { err.textContent = 'Enter the 6-digit code.'; return; }
      setBusy(codeForm, true, 'Verifying…');
      try { const r = await doVerify(pendingEmail, code); if (!r.ok) err.textContent = r.message; } catch (_) { err.textContent = 'Network error. Try again.'; } finally { setBusy(codeForm, false); }
    });
    pwForm && pwForm.addEventListener('submit', async (e) => {
      e.preventDefault(); err.textContent = '';
      const pwd = (pwForm.querySelector('input').value || '').trim();
      if (!pwd) { err.textContent = 'Enter the admin password.'; return; }
      setBusy(pwForm, true, 'Signing in…');
      try { const r = await attemptPassword(pwd); if (!r.ok) err.textContent = r.message; } catch (_) { err.textContent = 'Network error.'; } finally { setBusy(pwForm, false); }
    });
    root.addEventListener('click', async (e) => {
      const act = e.target && e.target.getAttribute && e.target.getAttribute('data-act');
      if (!act) return;
      e.preventDefault();
      if (act === 'change-email' || act === 'back-email') setAuthStep('email');
      else if (act === 'toggle-bg') setAuthStep('password');
      else if (act === 'resend') {
        err.textContent = ''; const btn = e.target; const old = btn.textContent;
        btn.disabled = true; btn.textContent = 'Sending…';
        try { await authApi('/auth/request', { email: pendingEmail }); btn.textContent = 'Code sent'; } catch (_) { btn.textContent = old; }
        setTimeout(() => { btn.disabled = false; btn.textContent = old; }, 4000);
      }
    });
  }

  function logout() {
    if (cfg.noGate) { try { sessionStorage.removeItem('agpe_password'); } catch (e) {} window.location.reload(); return; }
    setSession(null); setStoredPwd(''); showAuth('email');
  }

  /* Tool nav, dropdown filtered to the caller's allowed sections
     (allowed === null / undefined → show every tool). */
  function renderToolNav(allowed) {
    const sel = $('#ag-admin-tool-nav');
    if (!sel || !Array.isArray(cfg.tools)) return;
    const list = cfg.tools.filter(t => sectionAllowed(t.section, allowed === undefined ? null : allowed));
    sel.innerHTML = list.map(t => `<option value="${t.url || ''}"${t.current ? ' selected' : ''}>${t.name}</option>`).join('');
    if (!sel._wired) { sel.addEventListener('change', () => { if (sel.value) window.location.href = sel.value; }); sel._wired = true; }
  }

  /* Public API */
  window.AG_ADMIN = {
    api: api,
    toast: toast,
    logout: logout,
    session: getSession,
    /* The staff "tool password" the auth Worker hands back after OTP login.
       Direct-path tools that can't be proxied (e.g. Library/Plans, which hit
       the ag-library Worker with query-param auth) read this so a signed-in
       user isn't asked to type the password again. */
    pwd: getStoredPwd,
    onReady: (fn) => { if (authed) { fn(); } else { window.addEventListener('ag-admin-ready', () => fn(), { once: true }); } }
  };

  /* Force the admin shell's body bg to win against the storefront's
     main.css. Inline styles override external CSS regardless of
     specificity / !important, so this is the most resilient fix. */
  function lockBodyBackground() {
    try {
      const bg = getComputedStyle(document.body).getPropertyValue('--ag-bg').trim() || '#f4f6f9';
      const fg = getComputedStyle(document.body).getPropertyValue('--ag-text').trim() || '#1a1d21';
      document.body.style.setProperty('background', bg, 'important');
      document.body.style.setProperty('color', fg, 'important');
      document.documentElement.style.setProperty('background', bg, 'important');
    } catch (_) {}
  }

  /* Boot */
  document.addEventListener('DOMContentLoaded', async () => {
    lockBodyBackground();
    renderToolNav(null); // provisional full nav; re-gated after auth
    wireAuth();

    /* Chrome-only mode (e.g. blog editor owns its own login). */
    if (cfg.noGate) { hideAuthOverlay(); revealTools(); return; }

    /* Cached OTP session → validate + refresh permissions (catches
       revocation / section changes) via the auth Worker. */
    const session = getSession();
    if (session && session.token && (!session.exp || session.exp > nowS()) && authUrl) {
      try {
        const { data } = await authApi('/auth/whoami', {}, session.token);
        if (data && data.ok) {
          const fresh = { token: session.token, email: data.email, role: data.role, sections: data.sections || [], canManage: !!data.canManage, exp: session.exp };
          setSession(fresh);
          if (data.toolPw) setStoredPwd(data.toolPw);
          enterAdmin(fresh);
          return;
        }
      } catch (_) { /* fall through to login */ }
      setSession(null);
    }

    /* Break-glass persistence: a stored shared password (no OTP session)
       still silently re-auths, exactly like before. */
    if (getStoredPwd()) {
      try { const r = await api('admin_ping', {}); if (r && r.ok) { setSession(null); enterAdmin(null); return; } } catch (_) {}
      setStoredPwd('');
    }
    showAuth('email');
  });
})();
